<template>
  <div class="flex flex-col overflow-hidden">
    <div class="flex flex-row p-6 pb-2 items-center">
      <div class="flex text-sm text-text-default font-semibold">
        <span>{{ headerLabel }}</span>
        <span class="font-normal">&nbsp;{{ `(${members.length})` }}</span>
      </div>
      <v-popover
        interactive
        tag="div"
        content-tag="div"
        class="ml-auto"
        :role="'poper'"
        :trigger="'click'"
        :on-show="() => isElevated"
        :content-class="'add-invite-dropdown'"
      >
        <ButtonCustom
          v-if="(isProject || isManageSpaces) && !isGuestReader"
          :type="'important-custom'"
          :custom-icon-name="'user-plus'"
          :custom-icon-size="'16px'"
          :text="translate('add')"
          class="w-fit h-6 rounded-[4px] box-border py-0.5 px-2 text-sm"
          @click="openAddInviteDropdown"
        />
        <template #content="{ state, hide }">
          <ListDropdown
            v-if="state.isVisible"
            :width="240"
            class="project-settings-dropdown mr-5"
            :position="''"
            :items="addInviteDropdownItems"
            @click="handleAddInviteDropdownClick($event, hide)"
            @scroll="closeAddInviteDropdown(hide)"
            @closeEscape="closeAddInviteDropdown(hide)"
          />
        </template>
      </v-popover>
    </div>
    <vue-scroll
      :ops="{
        scrollPanel: { scrollingX: false },
        rail: { gutterOfSide: '4px' },
        bar: { size: '4px' },
      }"
    >
      <div class="flex flex-col gap-y-2 pb-2">
        <InfoSidebarMemberItem
          v-for="(member, index) in members"
          :key="index"
          :member="getMemberInTeamMembers(member.user_id, member)"
          :project="currentProject"
          :display-settings-button="displayMemberItemSettingsButtons"
          @removeMember="removeMemberFromProject"
        />
      </div>
    </vue-scroll>
    <Portal
      v-if="isConfirmationRevealed"
      to="root-widget"
    >
      <DoubleCheck
        v-click-outside="cancelConfirmation"
        :member="selectedMember"
        :members-array="currentProject.members"
        :project="currentProject"
        :show-user-info="false"
        @closeDoubleCheckPopup="cancelConfirmation"
        @leaveSpaceConfirmed="fulfilConfirmation"
      />
    </Portal>
  </div>
</template>

<script>
import DoubleCheck from './DoubleCheck';
import customIconsMixin from '@/mixins/customIconsMixin';
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import { getElementPositionByPointer } from '@/utilities/events/pointer';
import { convertStyleToNumber, getHeaderHeight } from '@/utilities/rootStyles';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import InfoSidebarMemberItem from '@/components/widgets/InfoSidebarMemberItem';
import getMemberLabel from '@/utilities/getMemberLabel';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ListDropdown from '@/components/widgets/ListDropdown';
import { ref, computed, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'InfoSidebarListOfMembers',
  components: {
    DoubleCheck,
    Portal,
    InfoSidebarMemberItem,
    ButtonCustom,
    ListDropdown,
  },
  mixins: [customIconsMixin],
  props: {
    members: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  setup() {
    const { setAndOpenAdjacentProject } = useSetAndOpenProject();
    const { isRevealed, reveal, confirm, cancel, onConfirm } = useConfirmDialog();
    const _this = getCurrentInstance();
    const showAddInviteDropdown = ref(false);
    const dropdownEvent = ref(null);
    function closeAddInviteDropdown(hideFn) {
      if (typeof hideFn === 'function') hideFn();
      showAddInviteDropdown.value = false;
    }
    const { isElevated, isGuestReader } = useCurrentRole();

    function openAddInviteDropdown(e) {
      dropdownEvent.value = e;
      if (!isElevated.value) {
        _this.proxy.openMembersDropdown(dropdownEvent.value || e);
        return;
      }
      showAddInviteDropdown.value = true;
    }

    const addInviteDropdownItems = computed(() => {
      return [
        {
          id: 'add-members',
          label: _this.proxy.translate('addExistingMembers'),
          customIconName: _this.proxy.iconPath('user-plus'),
        },
        {
          id: 'invite-members',
          label: _this.proxy.translate('inviteNewMembers'),
          customIconName: _this.proxy.iconPath('mail'),
        },
      ];
    });

    function handleAddInviteDropdownClick(item, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      if (item.id === 'add-members') {
        _this.proxy.openMembersDropdown(dropdownEvent.value);
      } else if (item.id === 'invite-members') {
        _this.proxy.$store.dispatch('setAddMemberPopup', true);
      }
      closeAddInviteDropdown();
    }

    function handlePopstate() {
      if (showAddInviteDropdown.value) {
        closeAddInviteDropdown();
      }
    }

    onMounted(() => {
      window.addEventListener('popstate', handlePopstate);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('popstate', handlePopstate);
    });

    return {
      isConfirmationRevealed: isRevealed,
      revealConfirmation: reveal,
      cancelConfirmation: cancel,
      onConfirmationDone: onConfirm,
      fulfilConfirmation: confirm,
      showAddInviteDropdown,
      closeAddInviteDropdown,
      openAddInviteDropdown,
      addInviteDropdownItems,
      handleAddInviteDropdownClick,
      setAndOpenAdjacentProject,
      isElevated,
      isGuestReader,
    };
  },
  data() {
    return {
      // used to pass to DoubleCheck component when removing members
      selectedMember: {},
      membersRefetched: false,
    };
  },
  computed: {
    displayMemberItemSettingsButtons() {
      return (this.isProject || this.isManageSpaces) && !this.isGuestReader;
    },
    disableAddMembers() {
      return this.projectId === '1';
    },
    isProject() {
      return this.$route.name === this.$constants.routeNames.space;
    },
    isManageSpaces() {
      return this.$route.name === this.$constants.manageSpacesRouteName;
    },
    headerLabel() {
      return this.translate(
        this.isProject || this.isManageSpaces ? 'members' : 'assignedInThisBoard'
      );
    },
    currentProject() {
      return this.isManageSpaces
        ? this.$store.getters.getSelectedProject
        : this.$store.getters.getCurrentProject;
    },
    projectId() {
      return this.isManageSpaces
        ? this.$store.getters.getSelectedProjectId
        : this.$store.getters.getCurrentProjectId;
    },
    addMembersDropdownShown() {
      return (
        this.$store.getters.getCurrentCardIdMemberDropdown === 'project' &&
        this.$store.getters.getAddMembersTarget.type === this.$constants.infoSidebarProject
      );
    },
  },
  beforeUnmount() {
    if (this.$store.getters.getAddMembersTarget.type === this.$constants.infoSidebarProject) {
      this.closeMembersDropdown();
    }
  },
  methods: {
    getMemberInTeamMembers(memberId, memberParam = {}) {
      const member = this.$store.getters.getMemberById(memberId);
      const memberKeysLength = Object.keys(member).length;
      if (!memberKeysLength.length && !this.membersRefetched) {
        this.$store.dispatch('fetchTeamMembers');
        this.membersRefetched = true;
      }
      return memberKeysLength ? member : memberParam;
    },
    async removeMemberFromProject(memberId) {
      this.selectedMember = this.getMemberInTeamMembers(memberId);
      const projectIdToRemoveFrom = this.projectId;

      const removingMyself = this.selectedMember.id === this.$store.getters.getUserId;
      if (removingMyself) {
        const { isCanceled } = await this.revealConfirmation();
        if (isCanceled) return;
      }

      const teamProjects = [...this.$store.getters.getTeamProjects];
      const projectIndex = this.$store.getters.getTeamProjects.findIndex(
        (project) => project.id === this.projectId
      );
      if (removingMyself) {
        delete teamProjects[projectIndex].is_member;
      }

      const memberToRemove = this.currentProject.members.find(
        (member) => member.user_id === this.selectedMember.id
      );

      if (removingMyself && this.$store.getters.getCurrentProjectId === projectIdToRemoveFrom) {
        this.setAndOpenAdjacentProject(projectIdToRemoveFrom);
      }

      try {
        await this.$store.dispatch('removeMemberFromProject', {
          memberToRemove,
          projectId: projectIdToRemoveFrom,
        });

        const projectName = this.currentProject.title || '';
        if (removingMyself) {
          await this.$store.dispatch('addUiNotification', {
            message: this.translate('youLeftNamedSpace', { name: projectName }),
            duration: 4000,
          });
          return;
        }

        const memberName = getMemberLabel(this.selectedMember, true);
        await this.$store.dispatch('addUiNotification', {
          message: `${memberName} ${this.translate('removedFrom')} ${projectName} ${this.translate(
            'space'
          ).toLowerCase()}`,
          duration: 4000,
        });
      } catch {
        await this.$store.dispatch('addUiNotification', {
          message: this.translate(
            removingMyself ? 'youCannotLeaveTheSpace' : 'cantRemoveMembersFromSpace'
          ),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
      }

      this.selectedMember = {};
    },
    closeMembersDropdown() {
      this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
      this.$store.dispatch('setCurrentCardMembersDropdown', {});
      this.$store.dispatch('setAddMembersTarget', {});
    },
    openMembersDropdown($event) {
      if (this.addMembersDropdownShown) {
        this.closeMembersDropdown();
        return;
      }

      this.$store.dispatch('setCurrentCardIdMemberDropdown', 'project');
      this.$store.dispatch('setCurrentCardMembersDropdown', this.currentProject);
      this.$store.dispatch('setAddMembersTarget', {
        id: this.projectId,
        type: this.$constants.infoSidebarProject,
      });

      const dropdownPosition = getElementPositionByPointer($event);
      const top = convertStyleToNumber(dropdownPosition.top);
      if (top > 0) dropdownPosition.top = `${top - getHeaderHeight()}px`;
      this.$store.dispatch('setHeaderMemberDropdownPosition', dropdownPosition);
    },
  },
};
</script>
